import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

const useStyles = makeStyles((theme) => ({
  versionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  envLabel: {
    width: 60,
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: '0.75rem',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
}));

interface ReleaseDialogProps {
  open: boolean;
  onClose: () => void;
  service: {
    name: string;
    versionUrls: Record<string, string>;
  } | null;
}

export const ReleaseDialog = ({ open, onClose, service }: ReleaseDialogProps) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);

  const [versions, setVersions] = useState<Record<string, string>>({});
  const [loadingVersions, setLoadingVersions] = useState(false);

  const [ecrTags, setEcrTags] = useState<string[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const [selectedEnv, setSelectedEnv] = useState('dev');
  const [selectedTag, setSelectedTag] = useState('');

  const [deploying, setDeploying] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const fetchVersions = useCallback(async () => {
    if (!service) return;
    setLoadingVersions(true);
    const baseUrl = await discoveryApi.getBaseUrl('service-versions');
    const envVersions: Record<string, string> = {};

    await Promise.all(
      ['dev', 'stage', 'prod'].map(async (env) => {
        const url = service.versionUrls[env];
        if (!url) return;
        try {
          const res = await backstageFetch(
            `${baseUrl}/probe?url=${encodeURIComponent(url)}`,
          );
          if (res.ok) {
            const data = await res.json();
            const v = data.version ?? data.Version;
            envVersions[env] = typeof v === 'string' ? v : 'error';
          } else {
            envVersions[env] = 'error';
          }
        } catch {
          envVersions[env] = 'error';
        }
      }),
    );

    setVersions(envVersions);
    setLoadingVersions(false);
  }, [service, discoveryApi, backstageFetch]);

  const fetchEcrTags = useCallback(async () => {
    if (!service) return;
    setLoadingTags(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('release-manager');
      const res = await backstageFetch(
        `${baseUrl}/ecr-tags?service=${encodeURIComponent(service.name)}`,
      );
      if (res.ok) {
        const data = await res.json();
        setEcrTags(data.tags ?? []);
      }
    } catch {
      // silent — autocomplete will just be empty
    } finally {
      setLoadingTags(false);
    }
  }, [service, discoveryApi, backstageFetch]);

  useEffect(() => {
    if (open && service) {
      setSelectedEnv('dev');
      setSelectedTag('');
      setVersions({});
      setEcrTags([]);
      fetchVersions();
      fetchEcrTags();
    }
  }, [open, service, fetchVersions, fetchEcrTags]);

  const handleDeploy = async () => {
    if (!service || !selectedTag) return;
    setDeploying(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('release-manager');
      const res = await backstageFetch(`${baseUrl}/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service: service.name,
          environment: selectedEnv,
          tag: selectedTag,
        }),
      });
      if (res.ok) {
        setSnackbar({
          open: true,
          message: `Deployment triggered: ${service.name}@${selectedTag} -> ${selectedEnv}`,
          severity: 'success',
        });
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnackbar({
          open: true,
          message: data.error ?? `Deployment failed (${res.status})`,
          severity: 'error',
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to trigger deployment',
        severity: 'error',
      });
    } finally {
      setDeploying(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !deploying && onClose()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Deploy {service?.name}
        </DialogTitle>
        <DialogContent>
          <div className={classes.section}>
            <Typography variant="subtitle2" gutterBottom>
              Current Versions
            </Typography>
            {loadingVersions ? (
              <CircularProgress size={20} />
            ) : (
              ['dev', 'stage', 'prod'].map((env) => (
                <div key={env} className={classes.versionRow}>
                  <Typography className={classes.envLabel}>{env}</Typography>
                  {versions[env] ? (
                    <Chip
                      label={versions[env]}
                      size="small"
                      variant="outlined"
                      style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      color={versions[env] === 'error' ? 'secondary' : 'default'}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      &mdash;
                    </Typography>
                  )}
                </div>
              ))
            )}
          </div>

          <div className={classes.section}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Target Environment</FormLabel>
              <RadioGroup
                row
                value={selectedEnv}
                onChange={(e) => setSelectedEnv(e.target.value)}
              >
                <FormControlLabel value="dev" control={<Radio />} label="Dev" />
                <FormControlLabel value="stage" control={<Radio />} label="Stage" />
                <FormControlLabel value="prod" control={<Radio />} label="Prod" />
              </RadioGroup>
            </FormControl>
          </div>

          <div className={classes.section}>
            <Autocomplete
              freeSolo
              options={ecrTags}
              loading={loadingTags}
              value={selectedTag}
              onInputChange={(_e, value) => setSelectedTag(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Image Tag"
                  placeholder="Select or type a tag (e.g. v0.11.0)"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTags ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={deploying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeploy}
            disabled={deploying || !selectedTag}
            startIcon={
              deploying ? <CircularProgress size={16} /> : undefined
            }
          >
            Rollout
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
